import AppRoutes from "../../../routes/AppRoutes";
import AdminDashboardItem from "../../../types/admin/dashboard/AdminDashboardItem";
import {
  faUsers,
  faRoute,
  faFileUpload,
  faUserShield,
  faGrid2Plus,
  faChartLine,
  faSitemap,
  faEnvelope,
  faSliders,
  faHeartPulse,
} from "@fortawesome/pro-solid-svg-icons";
import {
  faUsers as faUsersRegular,
  faRoute as faRouteRegular,
  faFileUpload as faFileUploadRegular,
  faUserShield as faUserShieldRegular,
  faGrid2Plus as faGrid2PlusRegular,
  faChartLine as faChartLineRegular,
  faFolderUser,
  faSitemap as faSitemapRegular,
  faEnvelope as faEnvelopeRegular,
  faSliders as faSlidersRegular,
  faHeartPulse as faHeartPulseRegular,
} from "@fortawesome/pro-regular-svg-icons";
import { faFolderUser as faFolderUserLight } from "@fortawesome/pro-light-svg-icons";
import ttModulesList from "../../../config/modules/ttModulesList";
import { ClientPermissionMode } from "../../../types/access/ClientPermissionMode";

const TILE_KEY_PREFIX = "Pages.Admin.Dashboard.Tiles";

export function getAdminDashboardItems(
  permissionMode: ClientPermissionMode,
): AdminDashboardItem[] {
  return [
    {
      id: "user-management",
      section: "USERS",
      title: "User Management",
      titleKey: `${TILE_KEY_PREFIX}.UserManagement.Title`,
      description: "Search users, update details, and send journeys",
      descriptionKey: `${TILE_KEY_PREFIX}.UserManagement.Description`,
      icon: {
        default: faUsersRegular,
        hover: faUsers,
      },
      to: AppRoutes.admin.userManagement.path,
      requiredModule: "USER-JOURNEY",
      allowedPermissions: ["ViewAllEmployees", "MANAGE-EMPLOYEES"],
    },
    {
      id: "data-health",
      section: "USERS",
      title: "Data Health",
      titleKey: `${TILE_KEY_PREFIX}.DataHealth.Title`,
      description: "Find incomplete user records and fix them in bulk",
      descriptionKey: `${TILE_KEY_PREFIX}.DataHealth.Description`,
      icon: {
        default: faHeartPulseRegular,
        hover: faHeartPulse,
      },
      to: AppRoutes.admin.dataHealth.path,
      requiredModule: "USER-JOURNEY",
      allowedPermissions: ["ViewAllEmployees", "MANAGE-EMPLOYEES"],
    },
    {
      id: "user-group-management",
      section: "USERS",
      title: "User Group Management",
      titleKey: `${TILE_KEY_PREFIX}.UserGroupManagement.Title`,
      description: "Create and manage user groups for your company",
      descriptionKey: `${TILE_KEY_PREFIX}.UserGroupManagement.Description`,
      icon: {
        default: faFolderUserLight,
        hover: faFolderUser,
      },
      to: AppRoutes.admin.userGroups.root,
      allowedPermissions: [
        "ManageUserGroups",
        "MANAGE-USER-GROUPS",
        "ViewUserGroups",
        "VIEW-USER-GROUPS",
      ],
    },
    {
      id: "role-management",
      section: "USERS",
      title: "Role Management",
      titleKey: `${TILE_KEY_PREFIX}.RoleManagement.Title`,
      description: "Create roles and assign permissions",
      descriptionKey: `${TILE_KEY_PREFIX}.RoleManagement.Description`,
      icon: {
        default: faUserShieldRegular,
        hover: faUserShield,
      },
      to: AppRoutes.admin.roles.path,
      allowedPermissions: ["MANAGE-ROLES"],
    },
    {
      id: "journey-management",
      section: "JOURNEYS_AND_COMMUNICATIONS",
      title: "Journey Management",
      titleKey: `${TILE_KEY_PREFIX}.JourneyManagement.Title`,
      description: "Configure journeys and view send history",
      descriptionKey: `${TILE_KEY_PREFIX}.JourneyManagement.Description`,
      icon: {
        default: faRouteRegular,
        hover: faRoute,
      },
      to: AppRoutes.admin.journeyManagement.path,
      requiredModule: "JOURNEY-CONFIG",
      allowedPermissions: ["JourneyAdministration", "JOURNEY-ADMIN"],
    },
    {
      id: "dashboard-management",
      section: "SYSTEM_CONFIGURATION",
      title: "Dashboard Management",
      titleKey: `${TILE_KEY_PREFIX}.DashboardManagement.Title`,
      description: "Edit employee and manager dashboard content",
      descriptionKey: `${TILE_KEY_PREFIX}.DashboardManagement.Description`,
      icon: {
        default: faGrid2PlusRegular,
        hover: faGrid2Plus,
      },
      to: AppRoutes.admin.dashboards.path,
      allowedPermissions:
        permissionMode == "LEGACY-PERMISSIONS"
          ? ["ManageContentDashboards"]
          : ["MANAGE-CONTENT-DASHBOARDS"],
    },
    {
      id: "brand-management",
      section: "SYSTEM_CONFIGURATION",
      title: "Brand Management",
      titleKey: `${TILE_KEY_PREFIX}.BrandManagement.Title`,
      description: "Manage brand settings and assigned assets",
      descriptionKey: `${TILE_KEY_PREFIX}.BrandManagement.Description`,
      icon: {
        default: faSitemapRegular,
        hover: faSitemap,
      },
      to: AppRoutes.admin.brands.path,
      allowedPermissions:
        permissionMode == "LEGACY-PERMISSIONS"
          ? ["ManageBrands"]
          : ["MANAGE-BRANDS"],
    },
    {
      id: "platform-configuration",
      section: "SYSTEM_CONFIGURATION",
      title: "Platform Configuration",
      titleKey: `${TILE_KEY_PREFIX}.PlatformConfiguration.Title`,
      description:
        "Manage company name, logo, help contact, maintenance mode and welcome message",
      descriptionKey: `${TILE_KEY_PREFIX}.PlatformConfiguration.Description`,
      icon: {
        default: faSlidersRegular,
        hover: faSliders,
      },
      to: AppRoutes.admin.platformConfiguration.path,
      allowedPermissions:
        permissionMode == "LEGACY-PERMISSIONS"
          ? []
          : ["PLATFORM-CONFIGURATION"],
    },
    {
      id: "analytics-configuration",
      section: "ANALYTICS",
      title: "Analytics Configuration",
      titleKey: `${TILE_KEY_PREFIX}.AnalyticsConfiguration.Title`,
      description: "Set up analytics data points and scoring",
      descriptionKey: `${TILE_KEY_PREFIX}.AnalyticsConfiguration.Description`,
      icon: {
        default: faChartLineRegular,
        hover: faChartLine,
      },
      to: AppRoutes.admin.datapoints.root,
      allowedPermissions:
        permissionMode == "LEGACY-PERMISSIONS"
          ? ["ConfigureAnalytics"]
          : ["CONFIGURE-ANALYTICS"],
    },
    {
      id: "external-score-uploader",
      section: "ANALYTICS",
      title: "External Score Uploader",
      titleKey: `${TILE_KEY_PREFIX}.ExternalScoreUploader.Title`,
      description: "Upload external scores for employees",
      descriptionKey: `${TILE_KEY_PREFIX}.ExternalScoreUploader.Description`,
      icon: {
        default: faFileUploadRegular,
        hover: faFileUpload,
      },
      to: AppRoutes.analytics.scoreUploader,
      allowedPermissions:
        permissionMode == "LEGACY-PERMISSIONS"
          ? ["CanUploadExternalScores"]
          : ["UPLOAD-EXTERNAL-SCORES"],
    },
    {
      id: "notifications",
      section: "JOURNEYS_AND_COMMUNICATIONS",
      title: "Notifications",
      titleKey: `${TILE_KEY_PREFIX}.Notifications.Title`,
      description: "Send email and push notifications to users",
      descriptionKey: `${TILE_KEY_PREFIX}.Notifications.Description`,
      icon: {
        default: faEnvelopeRegular,
        hover: faEnvelope,
      },
      to: AppRoutes.admin.notifications.root,
      allowedPermissions:
        permissionMode == "LEGACY-PERMISSIONS"
          ? ["ManageNotifications"]
          : ["MANAGE-NOTIFICATIONS"],
      requiredClientModule: ttModulesList.SelfServeNotifications,
    },
  ];
}
